import { ButtonDestructive, ButtonPrimary, ButtonSecondary } from "@nova/component-btn"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { Dispatch, SetStateAction, useState, useEffect, useMemo, useRef } from "react"
import {
    selectAnyListHasChanges,
    addContactToList,
    selectIsSavingChanges,
    selectContactListById,
    clearLocalChanges,
    selectChangedListIds,
    selectDeletedListIds,
    setIsSavingChanges,
    selectActiveMissionGroup,
    setActiveMissionGroupId,
    deleteList as softDeleteList,
    renameContactList,
    selectDoesListHaveChanges,
    updateMissionGroupIcon,
    updateMissionGroupAutoNotify,
    updatePasswordEnabled,
    updateMissionGroupPassword,
} from "./slice"
import { useContactListFunctions, useContactLists } from "./useContactListFunctions"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import {
    MissionGroupColor,
    missionGroupColors,
    MissionGroupIcon as TMissionGroupIcon,
    missionGroupIcons,
    ShareContact,
} from "@nova/schemas"
import { ToggleSwitch } from "@nova/component-toggle-switch"
import { useSavedContacts } from "./SavedContactsQueries"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { pluralize } from "@nova/util-strings"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { Contact } from "./Contact"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import store from "integrations/redux/store"
import { setGlobalNotificationContext } from "user/profile/slice"
import { useLoadingText } from "../../../useLoadingText"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { CreateMissionGroupModal } from "./MissionGroupModals"
import { MissionGroupIcon } from "./MissionGroupIcon"
import { ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { ExpandableQRCode } from "features/data-management/workspace/TODO-put-in-package/ExpandableQRCode"
import { Dropdown } from "@nova/component-dropdown"
import { MissionGroupsAppearanceSelector } from "./MissionGroupsAppearanceSelector"
import { useScreenWidthThreshold } from "@nova/toolkit-pure-hooks"
import { ScrollableArea } from "@nova/component-scrollable-area"
import {
    missionGroupUrl,
    ShareInactiveStreamLinkModal,
} from "features/multi-stream/ShareLiveStreamModal"
import { PasswordInput } from "@nova/component-password-input"
import { ContactInputSingleSelect } from "themes/default-tailwind/buttons/ContactInput"
import { fetchApiGateway } from "@nova/fetch-api-gateway"

const useMissionGroupsSidebarVersion = () => {
    const isCondensed = useScreenWidthThreshold(1300) === "below"
    return isCondensed ? "condensed" : "default"
}

export function MissionGroupsSettingsGroupsTabContent({
    setHasUnsavedChanges,
}: {
    setHasUnsavedChanges?: Dispatch<SetStateAction<boolean>>
}) {
    const dispatch = useAppDispatch()
    const activeGroup = useAppSelector(selectActiveMissionGroup)
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) || ""
    const confirmationModal = useConfirmationModalContext()
    const { contactLists, isFetching } = useContactLists()
    const { isEditor } = useWorkspacePermissions()
    const passwordInputRef = useRef<HTMLInputElement>(null)
    const [showEmptyPasswordError, setShowEmptyPasswordError] = useState(
        activeGroup?.passwordEnabled && !activeGroup.password
    )

    const anyListHasChanges = useAppSelector(selectAnyListHasChanges)
    useEffect(() => {
        setHasUnsavedChanges?.(anyListHasChanges)
    }, [anyListHasChanges, setHasUnsavedChanges])

    const [editingActiveGroupName, setEditingActiveGroupName] = useState(false)

    const sidebarVersion = useMissionGroupsSidebarVersion()

    if (isFetching) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }

    if (!contactLists.length) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="flex flex-col justify-center items-center max-w-[250px] gap-4">
                    <Icons.ExtraLargeIcon
                        Icon={Icons.ContactEmergency}
                        className="text-white-300"
                    />
                    <div className="flex flex-col justify-center items-center">
                        <div className="text-lg">No Mission Groups yet</div>
                        <div className="text-gray-200 text-center">
                            Create mission groups to easily notify multiple people to watch Live
                            Streams.
                        </div>
                    </div>
                    <ButtonPrimary
                        title="New Group"
                        LeftIcon={Icons.Plus}
                        className="w-full"
                        onClick={() => {
                            confirmationModal.confirm({
                                RenderComponent: CreateMissionGroupModal,
                            })
                        }}
                    />
                </div>
            </div>
        )
    }

    const streamLink = missionGroupUrl({ workspaceId, groupId: activeGroup?.id ?? "" })

    return (
        <div className="flex-1 flex gap-6 overflow-hidden">
            <div
                className={classNames("flex flex-col gap-4", {
                    "w-[256px]": sidebarVersion === "default",
                    "w-[56px]": sidebarVersion === "condensed",
                })}
            >
                {isEditor && (
                    <div className="flex flex-col gap-4 pr-4">
                        <ButtonSecondary
                            title={sidebarVersion === "default" ? "New Group " : ""}
                            LeftIcon={Icons.Plus}
                            onClick={() => {
                                confirmationModal.confirm({
                                    RenderComponent: CreateMissionGroupModal,
                                })
                            }}
                            className="!min-w-0 !px-0"
                        />
                        <div className="w-full h-px bg-gray-700" />
                    </div>
                )}
                <ScrollableArea shouldPreventScrollbarShifting>
                    <div className="flex flex-col gap-0.5 pr-4">
                        {contactLists.map((group) => {
                            return <MissionGroupListItem key={group.id} groupId={group.id} />
                        })}
                    </div>
                </ScrollableArea>
            </div>
            {activeGroup && (
                <div className="flex-1 flex flex-col gap-2 overflow-hidden">
                    <div className="flex items-center gap-2.5 overflow-hidden">
                        <Dropdown disabled={!isEditor}>
                            <Dropdown.Trigger as="div">
                                <div className="shrink-0 cursor-pointer hover:brightness-110">
                                    <MissionGroupIcon groupId={activeGroup.id} />
                                </div>
                            </Dropdown.Trigger>
                            <Dropdown.Menu
                                unstyled
                                className="w-[300px] rounded-xl bg-gray-700 shadow-dp4 overflow-hidden"
                            >
                                <MissionGroupsAppearanceSelector
                                    selectedColor={activeGroup.color}
                                    setSelectedColor={(color) => {
                                        dispatch(
                                            updateMissionGroupIcon({
                                                groupId: activeGroup.id,
                                                color: color as MissionGroupColor,
                                                icon: activeGroup.icon ?? missionGroupIcons[0],
                                            })
                                        )
                                    }}
                                    selectedIcon={activeGroup.icon}
                                    setSelectedIcon={(icon) => {
                                        dispatch(
                                            updateMissionGroupIcon({
                                                groupId: activeGroup.id,
                                                color: activeGroup.color ?? missionGroupColors[0],
                                                icon: icon as TMissionGroupIcon,
                                            })
                                        )
                                    }}
                                />
                            </Dropdown.Menu>
                        </Dropdown>

                        <div className="grow">
                            <ClickAwayListenerV2
                                onClickAway={() => {
                                    setEditingActiveGroupName(false)
                                }}
                            >
                                <div
                                    className={classNames("w-full truncate", {
                                        "cursor-text": !editingActiveGroupName,
                                    })}
                                    onClick={() => {
                                        if (!isEditor) return
                                        setEditingActiveGroupName(true)
                                    }}
                                >
                                    <RenamableText
                                        Tag="h5"
                                        id="active-mission-group-name"
                                        title={activeGroup.name}
                                        initialTitleIsPlaceholder={false}
                                        isRenaming={editingActiveGroupName}
                                        className="[&_h5]:text-base [&_h5]:text-white"
                                        onConfirmRenaming={(_, v) => {
                                            dispatch(
                                                renameContactList({
                                                    contactListId: activeGroup.id,
                                                    newName: v,
                                                })
                                            )
                                            setEditingActiveGroupName(false)
                                        }}
                                        onCancelRenaming={() => {
                                            setEditingActiveGroupName(false)
                                        }}
                                    />
                                </div>
                            </ClickAwayListenerV2>
                        </div>
                    </div>
                    <div className="flex-1 flex flex-col gap-4 overflow-hidden">
                        <div className="w-full h-px bg-gray-700" />
                        <div className="flex justify-between items-center">
                            <div className="flex flex-col gap-4">
                                <div
                                    className="flex gap-2 items-center w-fit cursor-pointer"
                                    onClick={() => {
                                        if (!isEditor) return
                                        dispatch(
                                            updateMissionGroupAutoNotify({
                                                groupId: activeGroup.id,
                                                autoNotify: !activeGroup.autoNotify,
                                            })
                                        )
                                    }}
                                >
                                    <div>Auto-notify when Live Stream starts</div>
                                    <ToggleSwitch
                                        isToggled={!!activeGroup.autoNotify}
                                        setIsToggled={() => {}}
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <ButtonPrimary
                                        title="Open Streams"
                                        RightIcon={Icons.OpenInNew}
                                        onClick={() => {
                                            window.open(streamLink, "_blank")
                                        }}
                                    />
                                    {isEditor && (
                                        <ButtonSecondary
                                            title="Share URL"
                                            RightIcon={Icons.Share}
                                            onClick={() => {
                                                confirmationModal.confirm<{
                                                    selectedEmails: string[]
                                                    selectedPhoneNumbers: string[]
                                                    selectedMissionGroupIds: string[]
                                                }>({
                                                    RenderComponent: ({
                                                        confirm,
                                                        cancel,
                                                        isPerformingAsyncConfirmCallback,
                                                    }) => (
                                                        <ShareInactiveStreamLinkModal
                                                            confirm={confirm}
                                                            cancel={cancel}
                                                            isPerformingAsyncConfirmCallback={
                                                                isPerformingAsyncConfirmCallback
                                                            }
                                                            type="mission-group"
                                                        />
                                                    ),
                                                    asyncConfirmCallback: async ({
                                                        selectedEmails,
                                                        selectedPhoneNumbers,
                                                        selectedMissionGroupIds,
                                                    }) => {
                                                        await fetchApiGateway(
                                                            "post /org/:orgId/mission-group/:groupId/share-url",
                                                            {
                                                                params: {
                                                                    orgId: workspaceId,
                                                                    groupId: activeGroup.id,
                                                                },
                                                                body: {
                                                                    emails: selectedEmails,
                                                                    phoneNumbers:
                                                                        selectedPhoneNumbers,
                                                                    missionGroupIds:
                                                                        selectedMissionGroupIds,
                                                                },
                                                            }
                                                        )
                                                    },
                                                })
                                            }}
                                        />
                                    )}
                                </div>
                                <div>{/* Spacer */}</div>
                                <div className="flex gap-2 items-center flex-wrap">
                                    <div className="shrink-0">Password protected</div>
                                    <ToggleSwitch
                                        className="shrink-0"
                                        isToggled={!!activeGroup.passwordEnabled}
                                        setIsToggled={(t) => {
                                            if (!isEditor) return
                                            dispatch(
                                                updatePasswordEnabled({
                                                    groupId: activeGroup.id,
                                                    passwordEnabled: t,
                                                })
                                            )
                                            if (t) {
                                                setTimeout(() => {
                                                    const input = passwordInputRef.current
                                                    if (input) {
                                                        input.focus()
                                                    }
                                                }, 10)
                                            } else {
                                                setShowEmptyPasswordError(false)
                                            }
                                        }}
                                    />
                                    {isEditor && !!activeGroup.passwordEnabled && (
                                        <div className="p-px w-full">
                                            <PasswordInput
                                                ref={passwordInputRef}
                                                value={activeGroup.password || ""}
                                                onChange={(e) =>
                                                    dispatch(
                                                        updateMissionGroupPassword({
                                                            groupId: activeGroup.id,
                                                            password: e.target.value,
                                                        })
                                                    )
                                                }
                                                onBlur={() => {
                                                    if (!activeGroup.password) {
                                                        setShowEmptyPasswordError(true)
                                                    }
                                                }}
                                                onFocus={() => {
                                                    setShowEmptyPasswordError(false)
                                                }}
                                            />
                                            {showEmptyPasswordError && (
                                                <div className="text-red-500 w-fit pt-2">
                                                    Please enter a password.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <ExpandableQRCode
                                title={activeGroup.name}
                                value={streamLink}
                                size={110}
                            />
                        </div>
                        <div className="w-full h-px bg-gray-700" />
                        <MissionGroupBody groupId={activeGroup.id} />
                    </div>
                </div>
            )}
        </div>
    )
}

function MissionGroupListItem({ groupId }: { groupId: string }) {
    const dispatch = useAppDispatch()
    const activeGroup = useAppSelector(selectActiveMissionGroup)
    const group = useAppSelector(selectContactListById(groupId))
    const isActive = activeGroup?.id === groupId
    const hasChanges = useAppSelector(selectDoesListHaveChanges(groupId))
    const sidebarVersion = useMissionGroupsSidebarVersion()
    if (!group) return null
    return (
        <div
            key={group.id}
            className={classNames(
                "w-full flex gap-2.5 p-2 items-center group transition-colors rounded-lg cursor-pointer relative",
                {
                    "hover:bg-white-100": !isActive,
                    "bg-white-200": isActive,
                }
            )}
            onClick={() => dispatch(setActiveMissionGroupId({ groupId: group.id }))}
        >
            <div className="shrink-0">
                <MissionGroupIcon groupId={group.id} size="24" />
            </div>
            {sidebarVersion === "default" && (
                <div className="flex gap-0.5 items-center overflow-hidden">
                    <div
                        className={classNames("transition-colors truncate", {
                            "text-white": isActive,
                            "text-gray-50 group-hover:text-white": !isActive,
                        })}
                    >
                        {group.name}
                    </div>
                    {hasChanges && <div className="shrink-0">*</div>}
                </div>
            )}
            {sidebarVersion === "condensed" && hasChanges && (
                <div className="absolute top-0 right-0 font-bold text-white-400">*</div>
            )}
        </div>
    )
}

function MissionGroupBody({ groupId }: { groupId: string }) {
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()
    const workspaceMembers = useAppSelector(selectWorkspaceMembers)
    const isSavingChanges = useAppSelector(selectIsSavingChanges)
    const { contacts } = useSavedContacts()
    const group = useAppSelector(selectContactListById(groupId))
    const contactTransformations = useMemo(() => {
        if (!group) return { sortedContacts: [], emails: [], phoneNumbers: [] }
        const workspaceMemberEmailsSet = new Set(workspaceMembers?.map((member) => member.email))
        const savedEmailsSet = new Set(
            contacts.filter((contact) => contact.email).map((contact) => contact.email!)
        )
        const savedPhoneNumbersSet = new Set(
            contacts.filter((contact) => contact.phone).map((contact) => contact.phone!)
        )
        const members = [] as { type: "email"; email: string }[]
        const savedEmails = [] as { type: "email"; email: string }[]
        const savedPhoneNumbers = [] as { type: "phone"; phoneNumber: string }[]
        const unrecognizedEmails = [] as { type: "email"; email: string }[]
        const unrecognizedPhoneNumbers = [] as {
            type: "phone"
            phoneNumber: string
        }[]
        group.contacts.forEach((contact) => {
            if (contact.type === "email") {
                if (workspaceMemberEmailsSet.has(contact.email)) {
                    members.push(contact)
                } else if (savedEmailsSet.has(contact.email)) {
                    savedEmails.push(contact)
                } else {
                    unrecognizedEmails.push(contact)
                }
            } else {
                if (savedPhoneNumbersSet.has(contact.phoneNumber)) {
                    savedPhoneNumbers.push(contact)
                } else {
                    unrecognizedPhoneNumbers.push(contact)
                }
            }
        })
        return {
            sortedContacts: [
                ...members.toSorted((a, b) => a.email.localeCompare(b.email)),
                ...savedEmails.toSorted((a, b) => a.email.localeCompare(b.email)),
                ...savedPhoneNumbers.toSorted((a, b) => a.phoneNumber.localeCompare(b.phoneNumber)),
                ...unrecognizedEmails.toSorted((a, b) => a.email.localeCompare(b.email)),
                ...unrecognizedPhoneNumbers.toSorted((a, b) =>
                    a.phoneNumber.localeCompare(b.phoneNumber)
                ),
            ] as ShareContact[],
            emails: [
                ...members.map((member) => member.email),
                ...savedEmails.map((email) => email.email),
                ...unrecognizedEmails.map((email) => email.email),
            ],
            phoneNumbers: [
                ...savedPhoneNumbers.map((phone) => phone.phoneNumber),
                ...unrecognizedPhoneNumbers.map((number) => number.phoneNumber),
            ],
        }
    }, [contacts, group, workspaceMembers])
    if (!group) return null
    return (
        <div className="grow overflow-hidden flex flex-col gap-1.5">
            {isEditor && (
                <div className="p-px" key={groupId}>
                    <ContactInputSingleSelect
                        enabledFeatures={["email", "phone"]}
                        onSelect={(c) => {
                            const email =
                                c.kind === "member"
                                    ? c.member.email
                                    : c.kind === "savedContactEmail"
                                      ? c.contact.email
                                      : c.kind === "rawEmail"
                                        ? c.value
                                        : undefined
                            const phone =
                                c.kind === "savedContactPhone"
                                    ? c.contact.phone
                                    : c.kind === "rawPhone"
                                      ? c.value
                                      : undefined
                            if (email) {
                                dispatch(
                                    addContactToList({
                                        contactListId: groupId,
                                        contact: {
                                            type: "email",
                                            email,
                                        } as ShareContact,
                                    })
                                )
                            } else if (phone) {
                                dispatch(
                                    addContactToList({
                                        contactListId: groupId,
                                        contact: {
                                            type: "phone",
                                            phoneNumber: phone,
                                        } as ShareContact,
                                    })
                                )
                            }
                        }}
                        disabled={isSavingChanges}
                    />
                </div>
            )}
            <div className="grow overflow-hidden flex">
                <ScrollableArea className="w-full">
                    <div className="grow flex flex-col w-full">
                        {contactTransformations.sortedContacts.map((contact) => (
                            <Contact
                                key={`${contact.email}-${contact.phoneNumber}`}
                                contact={contact}
                                contactListId={groupId}
                                allowRemoval={isEditor && group.contacts.length > 1}
                            />
                        ))}
                    </div>
                </ScrollableArea>
            </div>
        </div>
    )
}

export function MissionGroupsSettingsGroupsTabFooter() {
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()
    const { updateList, deleteList } = useContactListFunctions()
    const anyListHasChanges = useAppSelector(selectAnyListHasChanges)
    const listsWithChanges = useAppSelector(selectChangedListIds)
    const deletedLists = useAppSelector(selectDeletedListIds)
    const isSavingChanges = useAppSelector(selectIsSavingChanges)
    const savingText = useLoadingText(isSavingChanges, "Saving changes")
    const unsavedChangesText = useMemo(() => {
        let text = ""
        if (listsWithChanges.length) {
            text += `${pluralize({ count: listsWithChanges.length, str: "group" })} with changes`
        }
        if (deletedLists.length) {
            text += `${text ? " and " : ""}${pluralize({ count: deletedLists.length, str: "deleted list" })}`
        }
        return text ? `* ${text}` : ""
    }, [deletedLists.length, listsWithChanges.length])
    const confirmationModal = useConfirmationModalContext()
    const activeGroup = useAppSelector(selectActiveMissionGroup)

    return (
        <div className="w-full h-full flex items-center gap-2">
            <ButtonPrimary
                title="Save Changes"
                disabled={!isEditor || !anyListHasChanges || isSavingChanges}
                onClick={async () => {
                    dispatch(setIsSavingChanges(true))
                    await Promise.all([
                        ...listsWithChanges.map((listId) => {
                            const contactList = selectContactListById(listId)(store.getState())
                            if (!contactList) return
                            return updateList(
                                listId,
                                contactList.name,
                                contactList.contacts,
                                contactList.color ?? missionGroupColors[0],
                                contactList.icon ?? missionGroupIcons[0],
                                !!contactList.autoNotify,
                                !!contactList.passwordEnabled,
                                contactList.password
                            )
                        }),
                        ...deletedLists.map((listId) => deleteList(listId)),
                    ])
                    dispatch(clearLocalChanges())
                    dispatch(
                        setGlobalNotificationContext({
                            message: "Changes have been saved.",
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 3000,
                        })
                    )
                    dispatch(setIsSavingChanges(false))
                }}
            />
            {(anyListHasChanges || isSavingChanges) && (
                <small className="text-gray-300">
                    {isSavingChanges ? savingText : unsavedChangesText}
                </small>
            )}
            <div className="grow" />
            {activeGroup && (
                <ButtonDestructive
                    title="Delete Mission Group"
                    LeftIcon={Icons.Trash}
                    onClick={async () => {
                        const didConfirm = await confirmationModal.confirm({
                            RenderComponent: getGenericConfirmationModalComponent({
                                title: `Delete ${activeGroup.name}?`,
                                message: "Are you sure you want to delete this Mission Group?",
                                confirmText: "Delete Group",
                                leftConfirmIcon: Icons.Trash,
                            }),
                        })
                        if (didConfirm) {
                            dispatch(softDeleteList({ contactListId: activeGroup.id }))
                        }
                    }}
                    disabled={!isEditor}
                />
            )}
        </div>
    )
}
